import {LEAGUES} from '../constants/leagues';
import {FACTION_IDS_IMPLEMENTED} from './factions';

/**
 * Рейтинговые скины фракций ({factionId}_gold, {factionId}_diamond,
 * {factionId}_ruby) выдаются за 1 место в рейтинговом матче, если средняя
 * лига СОПЕРНИКОВ и собственная лига игрока — не ниже лиги скина (см.
 * calculateRankSkinRewards на сервере): игрок старшей лиги может добрать
 * пропущенный образ младшей.
 *
 * Порог по числу игроков живет здесь, а не в расчете: то же число показывается
 * игроку на странице фракции, и разъехаться они не должны.
 */
export const RANK_SKIN_MIN_PLAYERS = 4;

/**
 * Лиги, за которые выдаются рейтинговые образы, — по возрастанию, как
 * LEAGUE_CONFIGS. Кому нужен другой порядок (самое ценное первым), тот
 * разворачивает список сам.
 */
export const RANK_SKIN_LEAGUES = [LEAGUES.GOLD, LEAGUES.DIAMOND, LEAGUES.RUBY];

/**
 * Реализованные фракции, которым алмазный и рубиновый образы еще не нарисованы:
 * образы стоят заглушкой «СКОРО» и за победу не выдаются. Нарисуют — убрать
 * фракцию отсюда (куда класть файлы — см. RANK_SKIN_NEW_ART_FACTIONS).
 */
export const RANK_SKIN_NEW_ART_PENDING_FACTIONS = [];

const RANK_SKIN_NEW_ART_READY_FACTIONS = FACTION_IDS_IMPLEMENTED.filter(
  (factionId) => !RANK_SKIN_NEW_ART_PENDING_FACTIONS.includes(factionId)
);

/**
 * Для каких фракций у лиги уже нарисован новый арт (папки
 * public/img/factions/ranks_new и ranks_new_icons): null — для всех фракций,
 * массив — только для перечисленных. Прежний золотой и рубиновый арт стал
 * обычным покупным обличием ({factionId}_{rank}_legacy).
 *
 * У лиги со списком фракций нового арта нет у остальных фракций (алмазная и
 * рубиновая: только у реализованных, кроме RANK_SKIN_NEW_ART_PENDING_FACTIONS), у лиги не из списка — ни у кого. Такой
 * образ в карусели страницы фракции стоит заглушкой «СКОРО». Когда нарисуют
 * новый арт — положить арт в ranks_new/{factionId}_{rank}.jpg, иконки
 * 256×256 в ranks_new_icons/icon_{factionId}_{rank}.jpg и расширить запись
 * здесь.
 */
export const RANK_SKIN_NEW_ART_FACTIONS = {
  [LEAGUES.GOLD]: null,
  [LEAGUES.DIAMOND]: RANK_SKIN_NEW_ART_READY_FACTIONS,
  [LEAGUES.RUBY]: RANK_SKIN_NEW_ART_READY_FACTIONS,
};

/**
 * Лиги, у которых есть прежний арт (public/img/factions/ranks и ranks_icons):
 * на нем показываются образы, для которых нового арта нет, — рубиновые
 * нереализованных фракций (такой образ бывает только выданным вручную).
 */
export const RANK_SKIN_OLD_ART_LEAGUES = [LEAGUES.RUBY];

/**
 * @param {string} rank - лига образа (`gold` | `diamond` | `ruby`)
 * @param {string} factionId - фракция образа: у лиги новый арт может быть
 *   нарисован не для всех фракций
 * @returns {boolean} есть ли у образа новый арт
 */
export const checkIfRankSkinNewArtReady = (rank, factionId) => {
  if (!Object.prototype.hasOwnProperty.call(RANK_SKIN_NEW_ART_FACTIONS, rank)) {
    return false;
  }
  const factionIds = RANK_SKIN_NEW_ART_FACTIONS[rank];
  return factionIds === null || factionIds.includes(factionId);
};

/**
 * Есть ли у образа хоть какой-то арт — новый или прежний.
 *
 * @param {string} rank - лига образа
 * @param {string} factionId - фракция образа
 * @returns {boolean}
 */
export const checkIfRankSkinArtExists = (rank, factionId) => checkIfRankSkinNewArtReady(rank, factionId)
  || RANK_SKIN_OLD_ART_LEAGUES.includes(rank);

/**
 * Показывать ли рейтинговый образ в коллекции (раздел «Рейтинговые обличия»,
 * «Все обличия фракций»). Образ без нового арта там не показывается: его еще
 * не нарисовали. Исключение — уже открытый образ: у владельца он остается на
 * прежнем арте.
 *
 * @param {string} rank - лига образа (`gold` | `diamond` | `ruby`)
 * @param {boolean} isOwned - открыт ли образ у игрока, чью коллекцию смотрим
 * @param {string} factionId - фракция образа
 * @returns {boolean}
 */
export const checkIfRankSkinListed = (rank, isOwned, factionId) => Boolean(isOwned)
  || checkIfRankSkinNewArtReady(rank, factionId);

/**
 * Идентификатор рейтингового образа фракции.
 *
 * @param {string} factionId
 * @param {string} rank - лига образа
 * @returns {string} например `humans_diamond`
 */
export const getRankSkinId = (factionId, rank) => `${factionId}_${rank}`;

/**
 * Лига рейтингового образа по его идентификатору: последний сегмент id, если
 * это лига рейтинговых образов. У обычных обличий — null, в том числе у
 * «Наследия» (`humans_gold_legacy`: последний сегмент `legacy`).
 *
 * @param {string} [skinId]
 * @returns {string|null} лига (`gold` | `diamond` | `ruby`) или null
 */
export const getRankSkinLeague = (skinId) => {
  const separatorIndex = String(skinId || '').lastIndexOf('_');
  if (separatorIndex <= 0) {
    return null;
  }
  const rank = skinId.slice(separatorIndex + 1);
  return RANK_SKIN_LEAGUES.includes(rank) ? rank : null;
};
